import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTutorial } from '../../../contexts/TutorialContext';
import './Tutorial.css';

type Placement = 'top' | 'bottom' | 'left' | 'right' | 'center';

interface TooltipPosition {
  top: number;
  left: number;
  placement: Placement;
}

export const TutorialTooltip: React.FC = () => {
  const {
    isActive,
    getCurrentStep,
    getCurrentTutorial,
    currentStepIndex,
    getTotalSteps,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
  } = useTutorial();

  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const currentStep = getCurrentStep();
  const currentTutorial = getCurrentTutorial();
  const totalSteps = getTotalSteps();
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isFirstStep = currentStepIndex === 0;

  // Calculate tooltip position based on target element
  const calculatePosition = useCallback(() => {
    if (!currentStep) return;

    const targetSelector = currentStep.targetSelector;
    const desiredPlacement = currentStep.placement || 'bottom';

    // If no target, center the tooltip
    if (!targetSelector) {
      setPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
        placement: 'center',
      });
      return;
    }

    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) {
      setPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
        placement: 'center',
      });
      return;
    }

    const targetRect = targetElement.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const tooltipWidth = tooltipEl?.offsetWidth || 320;
    const tooltipHeight = tooltipEl?.offsetHeight || 200;
    const gap = 16; // Space between target and tooltip

    let top: number;
    let left: number;
    let placement: Placement = desiredPlacement;

    // Calculate position based on placement
    switch (desiredPlacement) {
      case 'top':
        top = targetRect.top - tooltipHeight - gap;
        left = targetRect.left + (targetRect.width - tooltipWidth) / 2;
        // Fall back to bottom if not enough space
        if (top < 10) {
          top = targetRect.bottom + gap;
          placement = 'bottom';
        }
        break;

      case 'bottom':
        top = targetRect.bottom + gap;
        left = targetRect.left + (targetRect.width - tooltipWidth) / 2;
        // Fall back to top if not enough space
        if (top + tooltipHeight > window.innerHeight - 10) {
          top = targetRect.top - tooltipHeight - gap;
          placement = 'top';
        }
        break;

      case 'left':
        top = targetRect.top + (targetRect.height - tooltipHeight) / 2;
        left = targetRect.left - tooltipWidth - gap;
        // Fall back to right if not enough space
        if (left < 10) {
          left = targetRect.right + gap;
          placement = 'right';
        }
        break;

      case 'right':
        top = targetRect.top + (targetRect.height - tooltipHeight) / 2;
        left = targetRect.right + gap;
        // Fall back to left if not enough space
        if (left + tooltipWidth > window.innerWidth - 10) {
          left = targetRect.left - tooltipWidth - gap;
          placement = 'left';
        }
        break;

      case 'center':
      default:
        top = window.innerHeight / 2 - tooltipHeight / 2;
        left = window.innerWidth / 2 - tooltipWidth / 2;
        placement = 'center';
        break;
    }

    // Ensure tooltip stays within viewport
    left = Math.max(10, Math.min(left, window.innerWidth - tooltipWidth - 10));
    top = Math.max(10, Math.min(top, window.innerHeight - tooltipHeight - 10));

    setPosition({ top, left, placement });
  }, [currentStep]);

  // Recalculate on step change
  useEffect(() => {
    if (isActive && currentStep) {
      // Allow time for spotlight to update
      const timer = setTimeout(calculatePosition, 150);
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep, currentStepIndex, calculatePosition]);

  // Recalculate on window resize
  useEffect(() => {
    if (!isActive) return;

    const handleResize = () => calculatePosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, calculatePosition]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          skipTutorial();
          break;
        case 'ArrowRight':
        case 'Enter':
          if (!isLastStep) {
            nextStep();
          } else {
            completeTutorial(false);
          }
          break;
        case 'ArrowLeft':
          if (!isFirstStep) {
            previousStep();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, isLastStep, isFirstStep, nextStep, previousStep, skipTutorial, completeTutorial]);

  if (!isActive || !currentStep || !position) {
    return null;
  }

  return (
    <div
      ref={tooltipRef}
      className={`tutorial-tooltip tutorial-tooltip--${position.placement}`}
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {/* Header */}
      <div className="tutorial-tooltip__header">
        <h3 className="tutorial-tooltip__title">{currentStep.title}</h3>
        <button
          className="tutorial-tooltip__close"
          onClick={() => skipTutorial()}
          aria-label="Close tutorial"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="tutorial-tooltip__content">
        <p>{currentStep.content}</p>
        {currentStep.actionText && (
          <p className="tutorial-tooltip__action-hint">
            <em>{currentStep.actionText}</em>
          </p>
        )}
      </div>

      {/* Progress indicator */}
      <div className="tutorial-tooltip__progress">
        <div className="tutorial-tooltip__dots">
          {Array.from({ length: totalSteps }, (_, i) => (
            <span
              key={i}
              className={`tutorial-tooltip__dot ${i === currentStepIndex ? 'tutorial-tooltip__dot--active' : ''} ${i < currentStepIndex ? 'tutorial-tooltip__dot--completed' : ''}`}
            />
          ))}
        </div>
        <span className="tutorial-tooltip__step-count">
          {currentStepIndex + 1} of {totalSteps}
        </span>
      </div>

      {/* Navigation */}
      <div className="tutorial-tooltip__nav">
        <button
          className="tutorial-tooltip__btn tutorial-tooltip__btn--secondary"
          onClick={() => skipTutorial()}
        >
          Skip
        </button>

        <div className="tutorial-tooltip__nav-main">
          {!isFirstStep && (
            <button
              className="tutorial-tooltip__btn tutorial-tooltip__btn--secondary"
              onClick={() => previousStep()}
            >
              Back
            </button>
          )}
          <button
            className="tutorial-tooltip__btn tutorial-tooltip__btn--primary"
            onClick={() => isLastStep ? completeTutorial(false) : nextStep()}
          >
            {isLastStep ? 'Got it!' : 'Next'}
          </button>
        </div>
      </div>

      {/* Don't show again option */}
      <div className="tutorial-tooltip__footer">
        <button
          className="tutorial-tooltip__dismiss"
          onClick={() => completeTutorial(true)}
        >
          Don't show this again
        </button>
      </div>
    </div>
  );
};

export default TutorialTooltip;
